import React, { createContext, useContext, useRef } from 'react';

interface FormContextProps {
  // Reference to the form container - RefObject.current can be HTMLDivElement or null
  formContainerRef: React.RefObject<HTMLDivElement>;
  // Function to set focus on the first input field and scroll to center
  focusAndCenterForm: () => void;
}

const FormContext = createContext<FormContextProps | undefined>(undefined);

export const FormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const formContainerRef = useRef<HTMLDivElement>(null);

  const focusAndCenterForm = () => {
    if (formContainerRef.current) {
      // Scroll the form into view with smooth behavior and center it
      formContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Find the first input field and focus it
      const firstInput = formContainerRef.current.querySelector('input, textarea, select') as HTMLElement;
      if (firstInput) {
        // Small delay to ensure the scroll completes before focusing
        setTimeout(() => firstInput.focus(), 500);
      }
    }
  };

  // Force TypeScript to correctly assign the type
  const value = {
    formContainerRef,
    focusAndCenterForm
  } as FormContextProps;

  return (
    <FormContext.Provider value={value}>
      {children}
    </FormContext.Provider>
  );
};

export const useForm = (): FormContextProps => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
}; 